import React, { useEffect, useState } from "react";
import Input from "./Input";
import Dropdown from "./Dropdown";
import Button from "./Button";
import Textarea from "./Textarea";
import RichTextArea from "./RichTextArea";

interface Option {
  label: string;
  value: string;
}

interface Validation {
  required?: boolean;
  optional?: boolean;
  min?: number;
  max?: number;
  regex?: string;
  message?: string;
}

export interface FormField {
  label: string;
  name: string;
  type:
    | "text"
    | "textarea"
    | "rich-textarea"
    | "number"
    | "email"
    | "password"
    | "dropdown";
  options?: Option[];
  onChange?: (value: string) => void; // ✅ supports per-field handler
  placeholder?: string;
  validations?: Validation;
  defaultValue?: string | number;
  disabled?: boolean;
}

interface FormProps {
  initialState?: Record<string, any>;
  fields: FormField[];
  onSubmit: (data: Record<string, any>) => void;
  onCancel?: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
}

const Form: React.FC<FormProps> = ({
  fields,
  onSubmit,
  onCancel,
  submitLabel = "Save",
  cancelLabel = "Cancel",
  loading = false,
  initialState = {},
}) => {
  // Combine defaults + provided initialState
  const computeInitialValues = () => {
    return fields.reduce((acc, field) => {
      const fromInitial = initialState?.[field.name];
      const fromDefault = field.defaultValue || "";
      return { ...acc, [field.name]: fromInitial ?? fromDefault };
    }, {} as Record<string, any>);
  };

  // ✅ Initialize only once on mount
  const [values, setValues] = useState<Record<string, any>>(() =>
    computeInitialValues()
  );

  const [errors, setErrors] = useState<Record<string, string>>({});

  // ✅ Re-initialize only when modal is reopened with new record (detect via key)
  useEffect(() => {
    // Compare if a new record is being edited (different _id or unique key)
    const currentKey = initialState?._id || initialState?.id || "";
    setValues((prev) => {
      const newValues = computeInitialValues();
      const prevKey = prev._id || "";
      return currentKey !== prevKey ? newValues : prev; // Only reset if truly new record
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialState?._id]);

  const validateField = (
    name: string,
    label: string,
    value: any,
    validations?: Validation
  ) => {
    if (!validations) return "";

    if (validations.required && !value) {
      return validations.message || `${label || name} is required`;
    }

    if (validations.min && value.length < validations.min) {
      return `Minimum ${validations.min} characters required`;
    }

    if (validations.max && value.length > validations.max) {
      return `Maximum ${validations.max} characters allowed`;
    }

    if (validations.regex && !new RegExp(validations.regex).test(value)) {
      return validations.message || `Invalid ${label || name}`;
    }

    return "";
  };

  /**
   * ✅ Updated handleChange
   * - Updates internal state
   * - Clears error for that field
   * - Fires the per-field `onChange` callback (useful for dependent dropdowns)
   */
  const handleChange = (name: string, value: any) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));

    // Fire the field-level onChange if defined
    const field = fields.find((f) => f.name === name);
    if (field?.onChange) {
      field.onChange(value);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};
    fields.forEach((field) => {
      const error = validateField(
        field.name,
        field.label,
        values[field.name],
        field.validations
      );
      if (error) newErrors[field.name] = error;
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      onSubmit(values);
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      {fields.map((field) => {
        const commonProps = {
          key: field.name,
          label: field.label,
          name: field.name,
          value: values[field.name],
          error: !!errors[field.name],
          helperText: errors[field.name],
          required: field.validations?.required,
          optional: field.validations?.optional,
        };

        if (field.type === "dropdown") {
          return (
            <Dropdown
              {...commonProps}
              options={field.options || []}
              disabled={field.disabled}
              onChange={(value) => handleChange(field.name, value)}
              fullWidth
            />
          );
        } else if (field.type === "textarea") {
          return (
            <Textarea
              {...commonProps}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                handleChange(field.name, e.target.value)
              }
            />
          );
        } else if (field.type === "rich-textarea") {
          return (
            <RichTextArea
              {...commonProps}
              onChange={(value: string) => handleChange(field.name, value)}
            />
          );
        }

        return (
          <Input
            {...commonProps}
            type={field.type}
            placeholder={field.placeholder}
            disabled={field.disabled}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange(field.name, e.target.value)
            }
          />
        );
      })}

      <div className="flex justify-end space-x-2 pt-2">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            {cancelLabel}
          </Button>
        )}
        <Button type="submit" isLoading={loading} disabled={loading}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};

export default Form;
